<script src="https://js.stripe.com/v3" async></script>
<script>
import { mapActions, mapMutations } from "vuex";
import { loadStripe } from "@stripe/stripe-js";

export default {
  data() {
    return {
      stripe: {},
      cardNumber: {},
      cardExpiry: {},
      cardCvc: {},
      loading: false,
    };
  },

  async created() {
    //Call the configuration functions and set the values in data so we can access/update them later
    const stripe = await this.configureStripe();
    // Configure the StripeElement with the stripe object
    const config = await this.configureStripeElements(stripe);
    this.stripe = config.stripe;
    this.cardNumber = config.cardNumber;
    this.cardExpiry = config.cardExpiry;
    this.cardCvc = config.cardCvc;
  },

  methods: {
    //Configure stripe with the private key and return it so we can use it in configureStripeElements
    // THIS NEEDS TO BE STORED DIFFERENTLY IN PROD
    async configureStripe() {
      let stripe = await loadStripe(process.env.VUE_APP_STRIPE_ELEMENTS_KEY);
      return stripe;
    },
    //Configure the individual stripe elements for Card Number, Expiration Date, CVC;
    //We use StripeJS library to be PCI Compliant when accepting credit card info
    //Each element is configured and styled and mounted to a div in the template
    async configureStripeElements(stripe) {
      let elements = stripe.elements();

      let cardNumber = await elements.create("cardNumber", {
        style: {
          base: {
            fontSize: "20px",
            textAlign: "center",
          },
        },
      });
      await cardNumber.mount(this.$refs.cardNumber);

      let cardExpiry = await elements.create("cardExpiry", {
        style: {
          base: {
            fontSize: "20px",
            textAlign: "center",
          },
        },
      });
      await cardExpiry.mount(this.$refs.cardExpiry);

      let cardCvc = await elements.create("cardCvc", {
        style: {
          base: {
            fontSize: "20px",
            textAlign: "center",
          },
        },
      });
      await cardCvc.mount(this.$refs.cardCvc);
      return {
        stripe,
        cardNumber,
        cardExpiry,
        cardCvc,
      };
    },
    async submit() {
      this.loading = true;
      //Using StripeJS to securely save credit card info and adhere to PCI Compliance
      //Create the pm_xxx ID; this result is an unassociated payment method token, we must save it to the customer
      this.stripe
        .createPaymentMethod({
          type: "card",
          // this.cardNumber implicitly includes values of the whole group (cardNumber, cardExpiry and cardCvc)
          card: this.cardNumber,
        })
        .then(async (result) => {
          if (result.paymentMethod) {
            //Take the resulting pm_xxx ID and save it to the customer
            let saved = await this.saveCreditCard({
              paymentMethodId: result.paymentMethod.id,
            });
            //If it was successful, emit the savedCC event so the parent component knows to reroute the user
            if (saved.ok) {
              this.loading = false;
              this.$emit("savedCC", saved);
            }
          } else {
            //Error handling for StripeJS errors
            switch (result.error.code) {
              case "incomplete_number":
                this.loading = false;
                this.pushToast({
                  title: "Error",
                  message: result.error.message,
                  variant: "danger",
                });
                break;
              case "invalid_number":
                this.loading = false;
                this.pushToast({
                  title: "Error",
                  message: result.error.message,
                  variant: "danger",
                });
                break;
              case "incomplete_expiry":
                this.loading = false;
                this.pushToast({
                  title: "Error",
                  message: result.error.message,
                  variant: "danger",
                });
                break;
              case "invalid_expiry_year_past":
                this.loading = false;
                this.pushToast({
                  title: "Error",
                  message: result.error.message,
                  variant: "danger",
                });
                break;
              case "incomplete_cvc":
                this.loading = false;
                this.pushToast({
                  title: "Error",
                  message: result.error.message,
                  variant: "danger",
                });
                break;
              default:
                this.loading = false;
                this.pushToast({
                  title: "Error",
                  message: "There was an error. Please try again",
                  variant: "danger",
                });
                break;
            }
          }
        });
    },
    ...mapActions(["saveCreditCard"]),
    ...mapMutations(["pushToast"]),
  },
};
</script>

<template>
  <div>
    <b-card>
      <b-form @submit.stop.prevent="submit">
        <b-form-group>
          <b-row>
            <b-col>
              <b-card>
                <div ref="cardNumber" />
              </b-card>
            </b-col>
          </b-row>
          <div class="spacer" />
          <b-row>
            <b-col style="width: 150px">
              <b-card>
                <div ref="cardExpiry" />
              </b-card>
            </b-col>
            <b-col style="width: 100px">
              <b-card>
                <div ref="cardCvc" />
              </b-card>
            </b-col>
          </b-row>
        </b-form-group>
        <div class="small-spacer" />
        <b-row align-h="center">
          <b-button size="lg" variant="primary" type="submit">
            <div v-if="!loading">Submit</div>
            <div v-else-if="loading">
              <b-spinner label="Spinning"></b-spinner>
            </div>
          </b-button>
        </b-row>
      </b-form>
      <div class="spacer" />
      <b-row>
        <b-col>
          <b-card-text class="disclaimer">
            Credit Cards are subject to a 5% fee per transaction
          </b-card-text>
          <b-card-text class="disclaimer">
            Bank Accounts are subject to a one-time $2 onboarding fee
          </b-card-text>
        </b-col>
        <b-col>
          <b-card-text class="small-text">
            Powered by
            <img class="icon" src="@/assets/stripe-logo-blue.png" />
          </b-card-text>
        </b-col>
      </b-row>
    </b-card>
  </div>
</template>

<style scoped>
.spacer {
  height: 25px;
}
.small-spacer {
  height: 10px;
}
.small-text {
  float: right;
  text-align: right;
  font-size: 10px;
  width: 200px;
}
.icon {
  height: 40px;
  width: 90px;
}
.disclaimer {
  font-size: 13px;
  margin-bottom: 3px;
}
</style>
